<template>
  <div>
    <a-form-item label="模组商品">
      <a-select
        :loading="loadingModuleProductOptions"
        @change="handleModuleProductChange"
        show-search
        :filter-option="filterOption"
        v-decorator="[
          'agents_product_id',
          { rules: [{ required: true, message: '请选择模组商品' }] },
        ]"
        placeholder="请选择客户已上架模组"
      >
        <a-select-option
          v-for="record in moduleProductOptions"
          :key="record.id"
          :value="record.id"
        >
          {{ record.module_producer_name }} / {{ record.module_model_name }}（{{ record.unit_price }} 元/片）
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="单价(元)">
      <a-input
        :value="unitPriceDisplay"
        disabled
      />
    </a-form-item>

    <a-form-item label="数量" required="required">
      <a-space>
        <a-input-number
          :min="1"
          :max="productCountMaxValue"
          :step="1"
          :precision="0"
          style="width: 120px"
          :formatter="parseProductCountInputValue"
          :parser="parseProductCountInputValue"
          @change="handleProductCountChange"
          v-decorator="['product_count', {
            rules: [
              { required: true, message: '请输入采购数量' }
            ]
          }]"
        />
        <span>片</span>
      </a-space>
    </a-form-item>

    <a-form-item label="产品备注">
      <a-textarea
        v-decorator="['product_remark', {
          rules: [
            { max: 200, message: '最多200个字符' },
          ]
        }]"
        :auto-size="{ minRows: 2, maxRows: 5 }"
      />
    </a-form-item>

    <a-descriptions
      :column="1"
      class="iotplt-descriptions iotplt-descriptions-max-6"
    >
      <a-descriptions-item label="费用(元)">
        {{ productFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="小计(元)">
        {{ totalFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="账户余额(元)">
        {{ balanceDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="可用额度(元)">
        {{ availableDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="已授信(元)">
        {{ creditDisplay }}
      </a-descriptions-item>
    </a-descriptions>

    <a-form-item label="新增授信(元)">
      <a-input-number
        :min="0"
        :max="10000000"
        :step="0.01"
        :precision="2"
        style="width: 100%;"
        v-decorator="['credit', {
          rules: [
            { required: true, message: '请输入授信额度' }
          ]
        }]"
      />
    </a-form-item>
  </div>
</template>

<script>
import { formatFloat } from '@/utils/filter'
import { findAgentsModuleProducts } from '@/api/agents_module_product'
import { findPreOrderItemLastRemarks } from '@/api/agent_pre_order_item'

export default {
  name: 'NewProductForm',
  props: {
    form: {
      type: Object,
      required: true
    },
    agentId: {
      type: Number,
      required: true
    },
    productTypeId: {
      type: Number,
      required: true
    },
    financeOverview: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    this.calculateProductFee = this.$lodash.debounce(this.calculateProductFee, 400)
    return {
      moduleProductOptions: [],
      loadingModuleProductOptions: false,
      selectedModuleProduct: null,

      productCountMaxValue: 10000000,
      productFeeDisplay: '',
      totalFeeDisplay: '',
      totalFeeValue: undefined
    }
  },
  computed: {
    balanceDisplay() {
      return this.formatMoney(this.financeOverview.balance)
    },
    availableDisplay() {
      return this.formatMoney(this.financeOverview.available)
    },
    creditDisplay() {
      return this.formatMoney(this.financeOverview.credit)
    },
    unitPriceDisplay() {
      if (!this.selectedModuleProduct) {
        return ''
      }
      return formatFloat(this.selectedModuleProduct.unit_price)
    }
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined) {
        return '--'
      }
      return formatFloat(value)
    },

    // 加载客户已上架模组（与卡预制选套餐一致）
    fetchModuleProductOptions() {
      if (!this.agentId) {
        this.moduleProductOptions = []
        return
      }
      this.loadingModuleProductOptions = true
      findAgentsModuleProducts({
        agent_id: this.agentId,
        effective: '1',
        page: 1,
        per_page: 1000
      }).then((res) => {
        if (res.code === 0) {
          this.moduleProductOptions = res.data || []
        }
        this.loadingModuleProductOptions = false
      })
    },

    handleModuleProductChange(productId) {
      this.selectedModuleProduct = this.moduleProductOptions.find((item) => item.id === productId) || null
      this.clearFeeDisplay()
      if (this.selectedModuleProduct) {
        this.calculateProductFee()
        this.fetchPreOrderItemLastRemarks()
      }
    },

    parseProductCountInputValue(val) {
      if (!val) {
        return ''
      }

      if (val < 1) {
        val = 1
      } else if (val > this.productCountMaxValue) {
        val = this.productCountMaxValue
      }
      return parseInt(val)
    },

    handleProductCountChange(productCount) {
      this.clearFeeDisplay()
      if (productCount > 0) {
        this.calculateProductFee()
      }
    },

    calculateProductFee() {
      if (!this.selectedModuleProduct) {
        return
      }
      this.form.validateFields(['product_count'], { force: true }, (err, values) => {
        if (!err) {
          const unitPrice = this.selectedModuleProduct.unit_price
          const totalFee = unitPrice * values.product_count
          this.productFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeValue = totalFee
        }
      })
    },

    clearFeeDisplay() {
      this.productFeeDisplay = ''
      this.totalFeeDisplay = ''
      this.totalFeeValue = undefined
    },

    fetchPreOrderItemLastRemarks() {
      if (!this.selectedModuleProduct) {
        return
      }
      findPreOrderItemLastRemarks({
        agent_id: this.agentId,
        product_type_id: this.productTypeId,
        comm_module_model_id: this.selectedModuleProduct.module_model_id
      }).then((res) => {
        if (res.code === 0) {
          this.form.setFieldsValue({
            product_remark: res.data.product_remark,
            delivery_remark: res.data.delivery_remark,
            invoice_remark: res.data.invoice_remark
          })
        }
      })
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    }
  }
}
</script>
